import { timeFormatDefaultLocale } from 'd3-time-format';
import moment from 'moment';

import config from '@@config';
import CONFIG from '@@lib/dateTime/config';
import { type LangCode } from '@@lib/i18n/constants';
import i18n from '@@lib/i18n/i18n';

export const isMetadataContentLocaleRoute = (pathname: string): boolean => {
    // Special case: /preview route (no UUID needed)
    if (pathname.startsWith('/preview/metadata')) {
        return true;
    }

    const allowedRoutes: string[] = [
        config.navigation.articles.uri,
        config.navigation.tickers.uri,
        config.navigation.slideshows.uri,
        config.navigation.embeds.uri,
        config.navigation.videos.uri,
        config.navigation.links.uri,
        config.navigation.breakingnews.uri,
    ];

    const uuidRegex =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89ab][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    const parts = pathname.split('/').filter(Boolean);

    // Try to find a UUID in the path
    const uuidIndex = parts.findIndex((part) => uuidRegex.test(part));
    if (uuidIndex === -1) {
        return false;
    }

    // Get base route as everything before UUID
    const baseRoute = '/' + parts.slice(0, uuidIndex).join('/');

    return allowedRoutes.includes(baseRoute);
};

const DATE_TIME: Record<LangCode, string> = {
    'de-CH': '%A, der %e. %B %Y, %X',
    'en-GB': '%A, %e %B %Y, %X',
    'fr-CH': '%A %e %B %Y, %X',
};

type TimeLocaleDefinition = Parameters<typeof timeFormatDefaultLocale>[0];

export const onUILocaleChanged = (locale: LangCode) => {
    i18n.changeLanguage(locale).catch((error) => {
        console.error('Failed to change language:', error);
    });

    // @ts-expect-error TS2345 Issue #4120
    moment.updateLocale(locale.toLowerCase(), CONFIG[locale.toLowerCase()]);

    const localeData = moment.localeData();

    // d3 is used by mui-x-charts, so we need to update its locale as well, in order to display
    // tick values of time axes correctly
    timeFormatDefaultLocale({
        dateTime: DATE_TIME[locale],
        date: localeData.longDateFormat('L'),
        time: localeData.longDateFormat('LTS'),
        periods: ['AM', 'PM'],
        days: localeData.weekdays() as TimeLocaleDefinition['days'],
        shortDays: localeData.weekdaysShort() as TimeLocaleDefinition['shortDays'],
        months: localeData.months() as TimeLocaleDefinition['months'],
        shortMonths: localeData.monthsShort() as TimeLocaleDefinition['shortMonths'],
    });
};
